import { Component, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FeedbackView } from '../feedback.service';
import { eventPageUrl } from '../feedback-links';
import { parseStaraCombined, StaraGrade } from './stara-feedback.model';
import { SportService } from 'app/services/sport/sport.service';

type CriterionRow = {
  id: string;
  grade: StaraGrade | null;
};

type ElementGroup = {
  id: string;
  criteria: CriterionRow[];
};

type FeedbackBlock = {
  key: string;
  text: string;
};

type JudgeCard = {
  judgeCode: string;
  hasContent: boolean;
  groups: ElementGroup[];
  feedbacks: FeedbackBlock[];
  collapsed: boolean;
};

type Program = {
  judges: JudgeCard[];
  collapsed: boolean;
};

type Round = {
  roundName: string;
  programs: Program[];
};

@Component({
  selector: 'stara-feedback-view',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './stara-feedback-view.component.html',
  styleUrls: ['./stara-feedback-view.component.scss'],
})
export class StaraFeedbackViewComponent implements OnInit {
  @Input() view: FeedbackView;

  rounds: Round[] = [];

  constructor(private sportService: SportService) {}

  ngOnInit() {
    this.applyData(this.view);
  }

  get eventUrl(): string {
    return eventPageUrl(this.view.eventId);
  }

  get totalPrograms(): number {
    return this.rounds.reduce((sum, round) => sum + round.programs.length, 0);
  }

  toggleProgram(program: Program) {
    program.collapsed = !program.collapsed;
  }

  toggleJudge(judge: JudgeCard) {
    judge.collapsed = !judge.collapsed;
  }

  anyExpanded(): boolean {
    return this.rounds.some((round) =>
      round.programs.some((program) => !program.collapsed || program.judges.some((judge) => !judge.collapsed))
    );
  }

  toggleAll() {
    const collapse = this.anyExpanded();
    this.rounds.forEach((round) =>
      round.programs.forEach((program) => {
        program.collapsed = collapse;
        program.judges.forEach((judge) => (judge.collapsed = collapse));
      })
    );
  }

  private applyData(view: FeedbackView) {
    this.rounds = view.rounds.map((round) => ({
      roundName: round.roundName,
      programs: round.programs.map((program) => ({
        collapsed: false,
        judges: [...program.feedbacks]
          .sort((a, b) => this.judgeNumber(a.judgeCode) - this.judgeNumber(b.judgeCode))
          .map((feedback) => this.toJudgeCard(feedback.judgeCode, feedback.feedback, program.feedbackFields ?? [])),
      })),
    }));
  }

  private judgeNumber(code: string): number {
    const match = code.match(/\d+$/);
    return match ? Number(match[0]) : 0;
  }

  private get elementGroupStructure(): { id: string; elementIds: string[] }[] {
    if (!this.view.judgingRulesProfileId) {
      return [];
    }
    const elementConfig = this.sportService.getJudgingRulesProfileAttr(this.view.judgingRulesProfileId, 'elementConfig');
    const groups = elementConfig?.elementGroups;
    if (!Array.isArray(groups)) {
      return [];
    }
    return groups.map((group) => ({
      id: group.id,
      elementIds: (group.elements ?? []).map((element) => element.id),
    }));
  }

  private toJudgeCard(judgeCode: string, content: string | null, fieldIds: string[]): JudgeCard {
    const parsed = parseStaraCombined(content, fieldIds);
    const groups: ElementGroup[] = this.elementGroupStructure.map((group) => ({
      id: group.id,
      criteria: group.elementIds.map((id) => ({
        id,
        grade: parsed.elements[id] ?? null,
      })),
    })).filter((group) => group.criteria.some((criterion) => criterion.grade != null));
    const feedbacks: FeedbackBlock[] = fieldIds.map((key) => ({
      key,
      text: parsed.feedback[key],
    }));
    return {
      judgeCode,
      hasContent: content != null,
      groups,
      feedbacks,
      collapsed: false,
    };
  }
}
